import { type InferResponseType, unwrap, withSignal } from "@nexxt/api-client";
import type { AccountTag, CrmAccountKind } from "@nexxt/contracts/crm";
import { queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the accounts of Guestlist (ADR-299 §6): the list with its search, kind and territory, the
// territories of the filter, the card 360 of one, and the pickers of a campaign; all from the projection of crm.

const accounts = api.crm.accounts;
const account = accounts[":organizationId"];

export type AccountSummary = InferResponseType<typeof accounts.$get, 200>["items"][number];
export type AccountCard = InferResponseType<typeof account.$get, 200>;
export type AccountCampaign = AccountCard["campaigns"]["items"][number];
export type AccountOrder = AccountCard["orders"]["items"][number];
export type AccountContact = AccountCard["contacts"]["items"][number];

export interface AccountListFilter {
  q?: string | undefined;
  kind?: CrmAccountKind | undefined;
  territory?: string | undefined;
  tag?: AccountTag | undefined;
  sort?: "warmth" | undefined;
}

/** A page of the list: enough accounts for a screen, the next ones on request. */
const LIST_PAGE = 50;

/** What ⌘K shows of a search. */
const FOUND = 5;

const keys = { all: ["accounts"] as const };

export const accountQueries = {
  ...keys,

  list: ({ q, kind, territory, tag, sort }: AccountListFilter, page: number) =>
    queryOptions({
      queryKey: [...keys.all, "list", { q, kind, territory, tag, sort }, page],
      queryFn: ({ signal }) =>
        unwrap(
          accounts.$get(
            {
              query: {
                page: String(page),
                pageSize: String(LIST_PAGE),
                ...(q && { q }),
                ...(kind && { kind }),
                ...(territory && { territory }),
                ...(tag && { tag }),
                ...(sort && { sort }),
              },
            },
            withSignal(signal),
          ),
        ),
    }),

  /** The territories change with the countries of party: a few minutes old is fresh enough. */
  territories: () =>
    queryOptions({
      queryKey: [...keys.all, "territories"],
      staleTime: 5 * 60_000,
      queryFn: ({ signal }) => unwrap(api.crm.territories.$get({}, withSignal(signal))),
    }),

  card: (organizationId: string) =>
    queryOptions({
      queryKey: [...keys.all, "card", organizationId],
      queryFn: ({ signal }) => unwrap(account.$get({ param: { organizationId } }, withSignal(signal))),
    }),

  /** What ⌘K finds by name, customers and brands: a few, the list has the rest (ADR-306). */
  find: (q: string) =>
    queryOptions({
      queryKey: [...keys.all, "find", q],
      queryFn: ({ signal }) => unwrap(accounts.$get({ query: { q, pageSize: String(FOUND) } }, withSignal(signal))),
    }),

  /** The accounts a picker finds by name: customers to invite, brands to invite to. */
  search: (kind: CrmAccountKind, q: string | undefined) =>
    queryOptions({
      queryKey: [...keys.all, "search", kind, q],
      enabled: q !== undefined,
      queryFn: ({ signal }) =>
        unwrap(accounts.$get({ query: { kind, pageSize: "20", ...(q && { q }) } }, withSignal(signal))),
    }),
};
